import ArticleImage from "../components/ArticleImage";
import { Link, useLocation, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import EditorialArticleHero from "../components/EditorialArticleHero";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import XPostEmbed from "../components/XPostEmbed";
import ArticleShareBlock from "../components/ArticleShareBlock";
import ArticleSocialBlock from "../components/ArticleSocialBlock";
import ArticleAd from "../ads/ArticleAd";
import InArticleAd from "../ads/InArticleAd";
import SidebarAd from "../ads/SidebarAd";
import { newsArticles } from "../data/newsArticles";
import { seoConfig } from "../data/seoConfig";
import PhysicalAIDatabasePanel from "../components/physical-ai/PhysicalAIDatabasePanel";
import { getPhysicalAIIntelligencePageByPath } from "../data/physicalAIIntelligence";

function ArticleNotFound() {
  return (
    <div className="article-page">
      <SEO
        title="Robotics Article Not Found TechniaHQRobot"
        description="The requested TechniaHQRobot robotics article could not be found."
        noindex
      />
      <section className="site-shell py-16">
      <div className="soft-card p-8">
        <h1 className="text-3xl font-black text-neutral-950">
          Robotics article not found
        </h1>
        <p className="mt-3 text-neutral-600">
          Open the Robotics News index to choose another article.
        </p>
        <Link to="/robotics-news" className="btn-primary mt-6">
          Open Robotics News
        </Link>
      </div>
      </section>
    </div>
  );
}

function ArticleVideo({ video }) {
  if (!video?.src) return null;

  return (
    <figure className="overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-neutral-950">
      <video
        controls
        playsInline
        preload="metadata"
        className="block h-auto w-full bg-black"
        aria-label={video.title || "Article video"}
      >
        <source src={video.src} type="video/mp4" />
        Your browser does not support HTML5 video.
      </video>
      <figcaption className="bg-white px-4 py-3 text-center text-sm font-black text-neutral-900">
        {video.caption || "@techniahqrobot"}
      </figcaption>
    </figure>
  );
}

function FactsBlock({ facts, title = "Key facts" }) {
  if (!facts?.length) return null;

  return (
    <section className="mt-8 rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5">
      <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">
        {title}
      </h2>
      <ul className="mt-4 grid gap-3 text-sm leading-7 text-neutral-700">
        {facts.map((fact) => (
          <li key={fact}>{fact}</li>
        ))}
      </ul>
    </section>
  );
}

function ArticleTable({ table, headingLevel = 2 }) {
  if (!table?.columns?.length || !table?.rows?.length) return null;
  const Heading = headingLevel === 3 ? "h3" : "h2";

  return (
    <section className="mt-8" aria-labelledby={table.id || undefined}>
      <Heading
        id={table.id || undefined}
        className="text-2xl font-black tracking-tight text-neutral-950"
      >
        {table.title}
      </Heading>
      {table.description && (
        <p className="mt-3 text-sm leading-7 text-neutral-600">
          {table.description}
        </p>
      )}
      <div className="mt-4 overflow-x-auto rounded-[1.25rem] border border-neutral-200" role="region" aria-label={table.title || "Article data table"} tabIndex="0">
        <table className="min-w-full border-collapse text-left text-sm">
          <thead className="bg-neutral-950 text-white">
            <tr>
              {table.columns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="min-w-[9rem] px-4 py-3 align-top font-black"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 bg-white">
            {table.rows.map((row, rowIndex) => (
              <tr key={`${table.id || table.title}-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={`${rowIndex}-${cellIndex}`}
                    className="px-4 py-3 align-top leading-6 text-neutral-700"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.note && (
        <p className="mt-3 text-xs leading-6 text-neutral-500">{table.note}</p>
      )}
    </section>
  );
}

function EventSchedule({ schedule, notice }) {
  if (!schedule?.length) return null;

  return (
    <div className="mt-6" aria-label="World Humanoid Robot Games 2026 daily schedule">
      <ol className="grid gap-4 sm:grid-cols-2">
        {schedule.map((item, index) => (
          <li
            key={item.date}
            className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5"
          >
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Day {index + 1}
            </p>
            <h3 className="mt-2 text-xl font-black tracking-tight text-neutral-950">
              <time dateTime={item.date}>{item.label}</time>
            </h3>
            <p className="mt-3 text-sm font-bold leading-6 text-neutral-800">
              {item.status}
            </p>
            <p className="mt-2 text-sm leading-7 text-neutral-600">
              {item.details}
            </p>
          </li>
        ))}
      </ol>
      {notice && (
        <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold leading-7 text-amber-950">
          {notice}
        </p>
      )}
    </div>
  );
}


function ArticleCodeBlocks({ blocks }) {
  if (!blocks?.length) return null;

  return (
    <div className="mt-6 grid gap-4">
      {blocks.map((block) => (
        <figure
          key={`${block.label}-${block.code}`}
          className="overflow-hidden rounded-[1.25rem] border border-neutral-800 bg-neutral-950"
        >
          <figcaption className="border-b border-neutral-800 px-4 py-3 text-xs font-black uppercase tracking-[0.12em] text-neutral-300">
            {block.label}
          </figcaption>
          <pre className="overflow-x-auto p-4 text-sm leading-7 text-neutral-100">
            <code>{block.code}</code>
          </pre>
        </figure>
      ))}
    </div>
  );
}

function EditorialList({ title, items, tone = "neutral", id }) {
  if (!items?.length) return null;
  const toneClasses = {
    neutral: "border-neutral-200 bg-neutral-50 text-neutral-800",
    amber: "border-amber-200 bg-amber-50 text-amber-950",
    blue: "border-blue-100 bg-blue-50 text-blue-950",
    green: "border-emerald-200 bg-emerald-50 text-emerald-950",
  };

  return (
    <section
      className={`mt-8 rounded-[1.5rem] border p-5 ${toneClasses[tone] || toneClasses.neutral}`}
      aria-labelledby={id || undefined}
    >
      <h2
        id={id || undefined}
        className="text-sm font-black uppercase tracking-[0.18em]"
      >
        {title}
      </h2>
      <ul className="mt-4 grid gap-3 text-sm leading-7">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

function normalizeEditorialText(value = "") {
  return String(value).replace(/\s+/g, " ").trim().toLowerCase();
}

function buildDisplayContent(article) {
  const seen = new Set();
  const remember = (value) => {
    const normalized = normalizeEditorialText(value);
    if (normalized.length >= 100) seen.add(normalized);
  };
  const keep = (value) => {
    const normalized = normalizeEditorialText(value);
    if (!normalized) return false;
    if (normalized.length < 100) return true;
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  };
  const uniqueList = (items = []) => (items || []).filter(keep);

  // The hero already exposes the article subtitle or description. Do not repeat it in the body.
  remember(article.subtitle || article.description);
  const trendReason = keep(article.trendReason) ? article.trendReason : null;
  const introduction = uniqueList(article.introduction || []);
  const featuredSnippet = keep(article.featuredSnippet) ? article.featuredSnippet : null;
  const keyFacts = uniqueList(article.keyFindings || article.keyFacts || []);
  const sections = (article.sections || []).map((section, index) => ({
    ...section,
    id: section.id || `section-${index + 1}`,
    paragraphs: uniqueList(section.paragraphs || []),
    subsections: (section.subsections || []).map((subsection) => ({
      ...subsection,
      paragraphs: uniqueList(subsection.paragraphs || []),
    })).filter((subsection) => subsection.paragraphs.length),
  })).filter((section) => (
    section.paragraphs.length || section.subsections.length ||
    section.bullets?.length || section.steps?.length || section.callout ||
    section.schedule?.length || section.table?.rows?.length ||
    section.codeBlocks?.length || section.sources?.length ||
    (Number.isInteger(section.videoIndex) && article.videos?.[section.videoIndex])
  ));
  const limitations = uniqueList(article.limitations || []);
  const conclusion = uniqueList(
    Array.isArray(article.conclusion)
      ? article.conclusion
      : article.conclusion
        ? [article.conclusion]
        : [],
  );

  return { trendReason, introduction, featuredSnippet, keyFacts, sections, limitations, conclusion };
}

function SourceList({ sources, sponsored = false }) {
  if (!sources?.length) return null;
  return (
    <div className="mt-5 grid gap-3">
      {sources.map((source) => (
        <a
          key={source.url}
          href={source.url}
          target="_blank"
          rel={sponsored ? "sponsored noopener noreferrer" : "noopener noreferrer"}
          className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 transition hover:border-blue-200"
        >
          <span className="block text-sm font-black text-neutral-900">
            {source.documentTitle || source.label}
          </span>
          <span className="mt-1 block text-xs leading-5 text-neutral-500">
            {[source.organization, source.date].filter(Boolean).join(" · ")}
          </span>
        </a>
      ))}
    </div>
  );
}

export default function RoboticsArticlePage() {
  const { slug } = useParams();
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, "") || "/";
  const resolvedSlug = slug || currentPath.split("/").filter(Boolean).pop();
  const article = newsArticles.find(
    (item) => item.path === currentPath || (item.slug === resolvedSlug && item.path),
  );

  if (!article) return <ArticleNotFound />;

  const articlePath = article.path || `/robotics-news/${article.slug}`;
  const intelligencePage = getPhysicalAIIntelligencePageByPath(currentPath);
  const displayContent = buildDisplayContent(article);
  const sections = displayContent.sections;
  const sourceLinks = article.sourceLinks || [];
  const comparisonTables = article.comparisonTables || [];
  const faqItems = article.faqs || [];
  const publicMethodology = (Array.isArray(article.methodology)
    ? article.methodology
    : article.methodology
      ? [article.methodology]
      : []
  ).filter(
    (paragraph) =>
      !/google search console export|content inventory/i.test(String(paragraph)),
  );
  const isPracticalArticle = articlePath.startsWith("/articles/");
  const breadcrumbs = [
    { label: "Home", to: "/" },
    {
      label: isPracticalArticle ? "Articles" : "Robotics News",
      to: "/robotics-news",
    },
    { label: article.title, to: articlePath },
  ];
  const isNeoHandArticle = article.slug === "1x-neo-25-dof-tendon-driven-hands";
  const contextualLinks = isNeoHandArticle
    ? [
        {
          label: "NEO humanoid robot profile",
          to: "/humanoid-robots/united-states#1x-neo",
        },
        { label: "Humanoid Robots", to: "/robot-types/humanoid-robots" },
        {
          label: "Humanoid robot hands",
          to: "/robot-types/humanoid-robot-hands",
        },
        {
          label: "Robotic grippers and end effectors",
          to: "/robot-types/robotic-grippers-and-end-effectors",
        },
      ]
    : [];

  const schema = {
    "@context": "https://schema.org",
    "@type": article.schemaType || "NewsArticle",
    headline: article.headline || article.title,
    description: article.description,
    ...(article.image ? { image: [article.image.startsWith("http") ? article.image : `${seoConfig.baseUrl}${article.image}`] } : {}),
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: "https://x.com/techniahqrobot",
    },
    publisher: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/`,
      logo: {
        "@type": "ImageObject",
        url: `${seoConfig.baseUrl}/techniahqrobot-logo-20260715.png`,
      },
    },
    articleSection: article.category || "Robotics",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${seoConfig.baseUrl}${articlePath}`,
    },
    keywords: article.keywords?.join(", "),
    about: (
      article.keywords || ["robotics", "humanoid robots", "physical AI"]
    ).map((name) => ({
      "@type": "Thing",
      name,
    })),
    ...(article.videos?.length
      ? {
          video: article.videos.map((video) => ({
            "@type": "VideoObject",
            name: video.title,
            description: video.title,
            contentUrl: `${seoConfig.baseUrl}${video.src}`,
            uploadDate: article.datePublished,
          })),
        }
      : {}),
  };

  const faqSchema = faqItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  const itemListSchema = article.rankedItems?.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: article.headline || article.title,
        numberOfItems: article.rankedItems.length,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        itemListElement: article.rankedItems.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          ...(item.url ? { url: item.url } : {}),
        })),
      }
    : null;

  const eventSchema = article.eventDetails
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: article.eventDetails.name || article.headline || article.title,
        description: article.eventDetails.description || article.description,
        startDate: article.eventDetails.startDate,
        endDate: article.eventDetails.endDate,
        eventStatus:
          article.eventDetails.eventStatus || "https://schema.org/EventScheduled",
        eventAttendanceMode:
          article.eventDetails.eventAttendanceMode ||
          "https://schema.org/OfflineEventAttendanceMode",
        url: `${seoConfig.baseUrl}${articlePath}`,
        ...(article.eventDetails.sameAs
          ? { sameAs: article.eventDetails.sameAs }
          : {}),
        ...(article.eventDetails.location
          ? {
              location: {
                "@type": "Place",
                ...article.eventDetails.location,
              },
            }
          : {}),
        ...(article.eventDetails.organizer
          ? {
              organizer: {
                "@type": "Organization",
                ...article.eventDetails.organizer,
              },
            }
          : {}),
      }
    : null;

  return (
    <div className="article-page">
      <SEO
        title={article.seoTitle || article.title}
        description={article.description}
        path={articlePath}
        type="article"
        image={article.image}
        imageAlt={article.imageAlt}
        publishedTime={article.datePublished}
        modifiedTime={article.dateModified || article.datePublished}
        author="TechniaHQRobot"
        section={article.category}
        tags={article.tags || article.keywords || []}
        schema={[
          buildBreadcrumbSchema(breadcrumbs),
          schema,
          ...(faqSchema ? [faqSchema] : []),
          ...(itemListSchema ? [itemListSchema] : []),
          ...(eventSchema ? [eventSchema] : []),
        ]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <section className="site-shell py-8 sm:py-10 lg:py-12">
        <EditorialArticleHero
          article={article}
          title={article.headline || article.title}
          subtitle={article.subtitle || article.description}
          category={article.category}
          author={article.authorName || "TechniaHQRobot"}
        />
      </section>

      <article className="site-shell py-10 sm:py-14">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="soft-card min-w-0 p-6 sm:p-8">
            <ArticleImage article={article} />
            {article.controlMethod && (
              <p className="mt-4 text-sm font-black text-blue-700">
                Control method {article.controlMethod}
              </p>
            )}

            {article.sponsoredDisclosure && (
              <p className="mt-5 rounded-[1.25rem] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold leading-6 text-amber-950">
                {article.sponsoredDisclosure}
              </p>
            )}

            {displayContent.trendReason && (
              <p className="mt-6 rounded-[1.5rem] border border-blue-100 bg-blue-50 px-5 py-4 text-base leading-8 text-blue-950">
                {displayContent.trendReason}
              </p>
            )}

            {!!displayContent.introduction.length && (
              <section className="mt-7" aria-labelledby="article-introduction">
                <h2 id="article-introduction" className="sr-only">
                  Introduction
                </h2>
                <div className="grid gap-4 text-base leading-8 text-neutral-700">
                  {displayContent.introduction.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            )}

            {article.editionNavigation?.items?.length > 0 && (
              <nav
                className="mt-7 rounded-[1.25rem] border border-neutral-200 bg-neutral-50 px-5 py-4"
                aria-label={article.editionNavigation.title || "Article edition history"}
              >
                <p className="text-xs font-black uppercase tracking-[0.16em] text-neutral-600">
                  {article.editionNavigation.title || "Edition history"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {article.editionNavigation.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      aria-current={article.path === item.path ? "page" : undefined}
                      className={`rounded-full border px-4 py-2 text-sm font-black transition ${
                        article.path === item.path
                          ? "border-neutral-950 bg-neutral-950 text-white"
                          : "border-neutral-300 bg-white text-neutral-800 hover:border-blue-400 hover:text-blue-700"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </nav>
            )}

            {(article.researchDate || article.informationVerifiedAsOf) && (
              <p className="mt-5 text-sm font-bold leading-7 text-neutral-600">
                {article.researchDate && <>Research date {article.researchDate}. </>}
                {article.informationVerifiedAsOf && (
                  <>Source review date recorded for this article {article.informationVerifiedAsOf}.</>
                )}
              </p>
            )}

            {displayContent.featuredSnippet && (
              <section
                className="mt-7 rounded-[1.5rem] border border-blue-100 bg-blue-50 p-5"
                aria-labelledby="direct-answer-summary"
              >
                <h2
                  id="direct-answer-summary"
                  className="text-sm font-black uppercase tracking-[0.16em] text-blue-800"
                >
                  Direct answer
                </h2>
                <p className="mt-3 text-base leading-8 text-blue-950">
                  {displayContent.featuredSnippet}
                </p>
              </section>
            )}

            {article.showTableOfContents && sections.length > 0 && (
              <nav
                className="mt-8 rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5"
                aria-labelledby="article-table-of-contents"
              >
                <h2
                  id="article-table-of-contents"
                  className="text-lg font-black tracking-tight text-neutral-950"
                >
                  Table of contents
                </h2>
                <ol className="mt-4 grid gap-2 text-sm leading-6 text-neutral-700 sm:grid-cols-2">
                  {sections.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#${section.id || `section-${index + 1}`}`}
                        className="font-bold transition hover:text-blue-700"
                      >
                        {section.heading}
                      </a>
                    </li>
                  ))}
                  {!!displayContent.limitations.length && <li><a href="#limitations-and-missing-information" className="font-bold transition hover:text-blue-700">Limits and conditions</a></li>}
                  {!!faqItems.length && <li><a href="#article-faq" className="font-bold transition hover:text-blue-700">Frequently asked questions</a></li>}
                  {!!sourceLinks.length && <li><a href="#sources-and-methodology" className="font-bold transition hover:text-blue-700">Sources</a></li>}
                </ol>
              </nav>
            )}

            <ArticleAd placement="top" articleSlug={article.slug} className="mt-8" />

            <XPostEmbed postUrl={article.tweetUrl} sponsored={Boolean(article.sponsoredDisclosure)} />
            <FactsBlock
              facts={displayContent.keyFacts}
              title={article.keyFindings ? "Key findings" : "Key facts"}
            />

            {intelligencePage && (
              <PhysicalAIDatabasePanel page={intelligencePage} embedded />
            )}

            {comparisonTables.map((table) => (
              <ArticleTable key={table.id || table.title} table={table} />
            ))}

            <EditorialList
              title="Evidence classification"
              items={article.evidenceClassification}
              tone="blue"
              id="evidence-classification"
            />

            {!!contextualLinks.length && (
              <section
                className="mt-8 rounded-[1.5rem] border border-blue-100 bg-blue-50 p-5"
                aria-labelledby="neo-hand-related-guides"
              >
                <h2
                  id="neo-hand-related-guides"
                  className="text-sm font-black uppercase tracking-[0.18em] text-blue-700"
                >
                  1X NEO and robot-hand guides
                </h2>
                <p className="mt-3 text-sm leading-7 text-neutral-700">
                  Use these pages to place the 25-DOF tendon-driven hand inside
                  the wider NEO humanoid system and compare it with other
                  humanoid hand architectures.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {contextualLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-black text-blue-800 transition hover:border-blue-400 hover:text-neutral-950"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 grid gap-9">
              {sections.map((section, index) => (
                <div key={section.heading}>
                  <section>
                    <h2
                      id={section.id || `section-${index + 1}`}
                      className="scroll-mt-24 text-2xl font-black tracking-tight text-neutral-950"
                    >
                      {section.heading}
                    </h2>
                    <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
                      {(section.paragraphs || []).map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                    {!!section.bullets?.length && (
                      <ul className="mt-5 grid gap-3 pl-5 text-base leading-8 text-neutral-700">
                        {section.bullets.map((item) => (
                          <li key={item} className="list-disc">{item}</li>
                        ))}
                      </ul>
                    )}
                    {!!section.steps?.length && (
                      <ol className="mt-5 grid gap-3 pl-5 text-base leading-8 text-neutral-700">
                        {section.steps.map((item) => (
                          <li key={item} className="list-decimal">{item}</li>
                        ))}
                      </ol>
                    )}
                    {section.callout && (
                      <p className="mt-5 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 font-semibold leading-7 text-blue-950">
                        {section.callout}
                      </p>
                    )}
                    <EventSchedule
                      schedule={section.schedule}
                      notice={section.scheduleNotice}
                    />
                    {section.table && <ArticleTable table={section.table} headingLevel={3} />}
                    <ArticleCodeBlocks blocks={section.codeBlocks} />
                    {!!section.subsections?.length && (
                      <div className="mt-6 grid gap-7">
                        {section.subsections.map((subsection) => (
                          <section key={subsection.heading}>
                            <h3 className="text-xl font-black tracking-tight text-neutral-950">
                              {subsection.heading}
                            </h3>
                            <div className="mt-3 grid gap-4 text-base leading-8 text-neutral-700">
                              {(subsection.paragraphs || []).map(
                                (paragraph) => (
                                  <p key={paragraph}>{paragraph}</p>
                                ),
                              )}
                            </div>
                          </section>
                        ))}
                      </div>
                    )}
                    {!!section.sources?.length && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        {section.sources.map((source) => (
                          <a
                            key={source.url}
                            href={source.url}
                            target="_blank"
                            rel={article.sponsoredDisclosure ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                            className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-black text-neutral-700 transition hover:border-blue-200 hover:text-blue-700"
                          >
                            {source.label}
                          </a>
                        ))}
                      </div>
                    )}
                    {Number.isInteger(section.videoIndex) &&
                      article.videos?.[section.videoIndex] && (
                        <div className="mt-6">
                          <ArticleVideo
                            video={article.videos[section.videoIndex]}
                          />
                        </div>
                      )}
                  </section>
                  <InArticleAd
                    index={index + 1}
                    totalSections={sections.length}
                    articleSlug={article.slug}
                  />
                </div>
              ))}
            </div>

            <EditorialList
              title="Limitations and missing information"
              items={displayContent.limitations}
              tone="amber"
              id="limitations-and-missing-information"
            />

            {!!displayContent.conclusion.length && (
              <section className="mt-10" aria-labelledby="article-conclusion">
                <h2
                  id="article-conclusion"
                  className="text-2xl font-black tracking-tight text-neutral-950"
                >
                  Conclusion
                </h2>
                <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
                  {displayContent.conclusion.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            )}

            {!!faqItems.length && (
              <section className="mt-10" aria-labelledby="article-faq">
                <h2
                  id="article-faq"
                  className="text-2xl font-black tracking-tight text-neutral-950"
                >
                  Frequently asked questions
                </h2>
                <div className="mt-5 grid gap-4">
                  {faqItems.map((item) => (
                    <details
                      key={item.question}
                      className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50 p-5"
                    >
                      <summary className="cursor-pointer font-black text-neutral-950">
                        {item.question}
                      </summary>
                      <p className="mt-3 text-sm leading-7 text-neutral-700">
                        {item.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {!!sourceLinks.length && (
              <section
                className="mt-10"
                aria-labelledby="sources-and-methodology"
              >
                <h2
                  id="sources-and-methodology"
                  className="text-2xl font-black tracking-tight text-neutral-950"
                >
                  Sources and methodology
                </h2>
                {!!publicMethodology.length && (
                  <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
                    {publicMethodology.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                )}
                <SourceList sources={sourceLinks} sponsored={Boolean(article.sponsoredDisclosure)} />
              </section>
            )}

            {!!article.internalLinks?.length && (
              <section className="mt-10" aria-labelledby="internal-links">
                <h2
                  id="internal-links"
                  className="text-2xl font-black tracking-tight text-neutral-950"
                >
                  Related TechniaHQRobot guides
                </h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {article.internalLinks.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {article.factCheck && (
              <section
                className="mt-10 rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-5"
                aria-labelledby="fact-check-report"
              >
                <h2
                  id="fact-check-report"
                  className="text-sm font-black uppercase tracking-[0.18em] text-emerald-950"
                >
                  Source review notes
                </h2>
                <p className="mt-3 text-sm font-black text-emerald-950">
                  {article.factCheck.verifiedDate
                    ? `Review date recorded ${article.factCheck.verifiedDate}`
                    : "No review date recorded"}
                </p>
                <div className="mt-4 grid gap-4 text-sm leading-7 text-emerald-950">
                  {!!article.factCheck.confirmed?.length && (
                    <div>
                      <h3 className="font-black">Supported by the cited sources</h3>
                      <ul className="mt-2 grid gap-2">
                        {article.factCheck.confirmed.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {!!article.factCheck.unconfirmed?.length && (
                    <div>
                      <h3 className="font-black">
                        Not confirmed or incomplete
                      </h3>
                      <ul className="mt-2 grid gap-2">
                        {article.factCheck.unconfirmed.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {!!article.factCheck.fastChanging?.length && (
                    <div>
                      <h3 className="font-black">Likely to change quickly</h3>
                      <ul className="mt-2 grid gap-2">
                        {article.factCheck.fastChanging.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            )}

            {!!article.verificationNotes?.length && (
              <section
                className="mt-10 rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5"
                aria-labelledby="verification-notes"
              >
                <h2
                  id="verification-notes"
                  className="text-sm font-black uppercase tracking-[0.18em] text-amber-900"
                >
                  Verification notes
                </h2>
                <ul className="mt-4 grid gap-3 text-sm leading-7 text-amber-950">
                  {article.verificationNotes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </section>
            )}

            <ArticleAd placement="bottom" articleSlug={article.slug} className="mt-10" />

            <ArticleShareBlock title={article.title} path={articlePath} />
            <ArticleSocialBlock className="mt-6" />

            {!!article.relatedLinks?.length && (
              <section
                className="mt-10 rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5"
                aria-labelledby="related-articles"
              >
                <h2
                  id="related-articles"
                  className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500"
                >
                  Related articles
                </h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {article.relatedLinks.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {(article.authorHandle || article.authorName) && (
              <div className="mt-10 border-t border-neutral-200 pt-6 text-sm font-bold text-neutral-600">
                Article by{" "}
                <a
                  href="https://x.com/techniahqrobot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 transition hover:text-neutral-950"
                >
                  {article.authorHandle || article.authorName}
                </a>
              </div>
            )}
          </div>

          <aside className="grid h-fit gap-5 lg:sticky lg:top-24">
            <SidebarAd articleSlug={article.slug} />

            {!!sourceLinks.length && (
              <div className="soft-card p-5">
                <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">
                  Sources
                </h2>
                <div className="mt-4 grid gap-3 text-sm font-bold leading-6">
                  {sourceLinks.map((source) => (
                    <a
                      key={source.url}
                      href={source.url}
                      target="_blank"
                      rel={article.sponsoredDisclosure ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                      className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
                    >
                      {source.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="soft-card p-5">
              <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">
                More robotics news
              </h2>
              <div className="mt-4 grid gap-2 text-sm font-bold">
                {newsArticles
                  .filter((item) => item.path && item.slug !== article.slug)
                  .slice(0, 5)
                  .map((item) => (
                    <Link
                      key={item.slug}
                      to={item.path}
                      className="rounded-2xl bg-neutral-50 px-4 py-3 text-neutral-800 transition hover:bg-blue-50 hover:text-blue-700"
                    >
                      {item.title}
                    </Link>
                  ))}
              </div>
            </div>
          </aside>
        </div>
      </article>
      <CTASection title="Follow TechniaHQRobot for sharp robotics explainers" />
    </div>
  );
}
